import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { ArrowLeft, Download, Lock } from 'lucide-react';
import { Button } from '../components';
import api from '../services/api';
import { getVerslag } from '../services/gespreksverslagApi';

/**
 * Eén gespreksverslag voor de professional zelf (migratie 176). Engine-verslagen
 * tonen alle stappen met leesbare labels; oude verslagen alleen hun tekst.
 * Vak A en Vak B staan hier wél: dit is de eigen weergave van de professional.
 */
const Blok: React.FC<{ titel: string; children: React.ReactNode }> = ({ titel, children }) => (
  <section className="border-t border-gray-200 pt-4 space-y-2">
    <h2 className="font-semibold text-gray-900">{titel}</h2>
    {children}
  </section>
);
const Html: React.FC<{ html: string | null }> = ({ html }) =>
  html ? <div className="prose prose-sm max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} /> : <p className="text-sm text-gray-500 italic">Niet ingevuld.</p>;

const ProfessionalVerslagDetailPage: React.FC = () => {
  const { dossierId, verslagId } = useParams<{ dossierId: string; verslagId: string }>();
  const { data: v, isLoading, error } = useQuery({ queryKey: ['verslag', verslagId], queryFn: () => getVerslag(verslagId!), enabled: !!verslagId, retry: false });

  const pdf = async () => {
    const res = await api.get(`/professional/dossiers/${dossierId}/verslagen/${verslagId}/pdf`, { responseType: 'blob' });
    window.open(URL.createObjectURL(res.data), '_blank', 'noopener');
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
      <Link to={`/professional/dossiers/${dossierId}`}><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />Terug naar dossier</Button></Link>
      {isLoading && <p className="text-sm text-gray-500">Laden…</p>}
      {error && <p className="text-sm text-red-700">{(error as any)?.response?.data?.message || 'Verslag niet gevonden.'}</p>}
      {v && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{v.titel}</h1>
              <p className="text-sm text-gray-600">
                {new Date(v.datum).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                {v.soort === 'engine' && <> · Sjabloon {v.sjabloon} · {v.sjabloon_label}{v.case_code && <> · <span className="font-mono">{v.case_code}</span></>}</>}
              </p>
            </div>
            {dossierId && <Button variant="outline" onClick={pdf}><Download className="w-4 h-4 mr-2" />PDF</Button>}
          </div>

          {v.soort === 'legacy' ? (
            <p className="whitespace-pre-wrap text-sm text-gray-800">{v.content || 'Dit verslag is gemaakt met het oude formulier; open de PDF voor alle velden.'}</p>
          ) : (
            <>
              {v.sjabloon === 'C' ? (
                <Blok titel="Doel van de vitaliteitssessie"><p className="text-sm">{v.doelen.join(', ')}</p></Blok>
              ) : (
                <>
                  {v.nieuw_incident && (
                    <Blok titel="Aard & rolverhouding">
                      <p className="text-sm">{v.categorieen.join(', ') || '—'}</p>
                      {v.rolverhouding && <p className="text-sm text-gray-600">Rolverhouding: {v.rolverhouding}</p>}
                      {v.geslacht_veroorzaker && <p className="text-sm text-gray-600">Geslacht beschuldigde: {v.geslacht_veroorzaker}</p>}
                    </Blok>
                  )}
                  <Blok titel="Kwalitatieve organisatie-update (Vak B)"><Html html={v.vak_b} /></Blok>
                  <Blok titel="Toegepaste interventies"><p className="text-sm">{v.interventies.join(', ') || '—'}</p></Blok>
                </>
              )}
              <Blok titel="Persoonlijke reflectie & huiswerk (Vak A)">
                <p className="flex gap-1 text-xs text-gray-500"><Lock className="w-3.5 h-3.5" aria-hidden />Alleen voor de medewerker; nooit voor de werkgever.</p>
                <Html html={v.vak_a} />
              </Blok>
              {v.deel5 && (
                <Blok titel="Management-update (Deel 5)">
                  <p className="text-sm"><span className="font-medium">Arbo-kader:</span> {v.deel5.arbo_kader}</p>
                  {v.deel5.fte_belastbaarheid != null && (
                    <p className="text-sm"><span className="font-medium">FTE-belastbaarheid:</span> {v.deel5.fte_belastbaarheid}% · <span className="font-medium">Verzuim:</span> {v.deel5.verwachte_verzuimreductie}</p>
                  )}
                  <p className="whitespace-pre-wrap text-sm">{v.deel5.werkvloer_advies}</p>
                  <p className="text-xs text-gray-500">
                    Toestemming medewerker: {v.toestemming === 'gegeven' ? 'gegeven — HR ziet deze update' : v.toestemming === 'gevraagd' ? 'gevraagd — HR ziet nog niets' : 'verlopen — niet gedeeld'}
                  </p>
                </Blok>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfessionalVerslagDetailPage;
